import './tailwind.scss';
import './global.scss';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
	BrowserRouter,
	Routes,
	Route,
	Navigate,
	useLocation,
} from 'react-router-dom';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { useStore } from './store';
import { authSocket } from 'services/socket';

// Common components
import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import Signin from 'components/Signin/Signin';
import Notifications from 'components/Notifications/Notifications';

// Pages
import Home from 'pages/Home/Home';
import Signup from 'pages/Auth/Signup';
import Profile from 'pages/Profile/Profile';
import JobCreate from 'pages/JobCreate/JobCreate';
import JobsDetails from 'pages/JobDetails/JobDetails';
import MyOrders from 'pages/MyOrders/MyOrders';
import OrderBids from 'pages/MyOrders/OrderBids';
import ProfileEdit from 'pages/ProfileEdit/ProfileEdit';
import Chats from 'pages/Chats/Chats';
import MyBids from 'pages/MyOrders/MyBids';
import AboutUs from 'pages/About/AboutUs';
import ForProviders from 'pages/About/ForProviders';
import HowItWorks from 'pages/About/HowItWorks';
import JobCreateProvider from 'pages/JobCreate/JobCreateProvider';
import JobReschedule from 'pages/JobEdit/JobReschedule';
import CalendarProvider from 'pages/Home/CalendarProvider';
import Waitlist from 'pages/Signup/Waitlist';
import Safety from 'pages/About/Safety';
import EmailUnsubscribe from 'pages/EmailUnsubscribe/EmailUnsubscribe';
import PatientsManage from 'pages/Patients/PatientsManage';
import PatientCreate from 'pages/Patients/PatientCreate';
import Landing from 'pages/Signup/Landing';
import CaregiverProfiles from 'pages/Signup/CaregiverProfiles';
import PartnerSignup from 'pages/Auth/PartnerSignup';
import UserSignup from 'pages/Signup/UserSignup';
import PatientAbout from 'pages/Patients/PatientAbout';
import AwaitingApprovalPage from 'pages/Signup/AwaitingApprovalPage';
import About from 'pages/About/About';
import Services from 'pages/About/Services';
import LandingTherapy from 'pages/About/LandingTherapy';
import CheckEligibility from 'pages/Eligibility/CheckEligibility';
import ConsentUpload from 'pages/Eligibility/ConsentUpload';
import JobCreateAthena from 'pages/JobCreate/JobCreateAthena';
import AthenaWelcome from 'pages/Home/AthenaWelcome';
import AthenaPatientInfo from 'pages/Home/AthenaPatientInfo';
import AthenaJobs from 'pages/MyOrders/AthenaJobs';
import Auth from 'pages/Signup/Auth';
import Policy from 'pages/Policy/Policy';
import Terms from 'pages/Policy/Terms';
import { sessionWatcher } from 'utils/sesstionWatcher';
import VerificationGates from 'pages/Signup/VerificationGates';
import Loader from 'components/Base/Loader';

const GoogleClientId = process.env.REACT_APP_GOOGLE_CLIENT;
const PaypalClientId = process.env.REACT_APP_PAYPAL_CLIENT;
const PaypalAttributionId = process.env.REACT_APP_PAYPAL_ATTRIBUTION;

const Router = observer(({ setCurrentRoute }) => {
	const store = useStore();
	const { isAuth, user, accessToPlatform } = store.auth;
	const [userInfoLoaded, setUserInfoLoaded] = useState(!isAuth);

	const location = useLocation();
	const hasSignedUp = localStorage.getItem('hasSignedUp');

	useEffect(() => {
		if (isAuth) {
			setUserInfoLoaded(false);
			store.auth.fetchUserInfo().finally(() => setUserInfoLoaded(true));
			sessionWatcher.start(store);
			// authSocket();
		} else {
			setUserInfoLoaded(true);
		}
	}, [isAuth, store.auth]);

	const shouldForceAthenaFlow = isAuth && userInfoLoaded && user.isEligibleAthena && !user.isCompleteProfile;
	const isFetchingUser = isAuth && !userInfoLoaded;

	useEffect(() => {
		setCurrentRoute(location.pathname);
	}, [location.pathname, setCurrentRoute])

	/** useEffect to trigger conversion event for sign up with google conversion tracking tag  */
	useEffect(() => {
		const addConversionScript = () => {
			// Initialize dataLayer if not defined
			window.dataLayer = window.dataLayer || [];

			// Create a script element
			const script = document.createElement('script');
			script.async = true;
			script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-11468082525';

			// Set up the onload callback to configure gtag
			script.onload = () => {
				function gtag() {
					window.dataLayer.push(arguments);
				}
				gtag('js', new Date());
				gtag('config', 'AW-11468082525');

				// Trigger the conversion event if on the home page after sign-up
				if (
					(location.pathname === '/providers' ||
						location.pathname === '/profile') &&
					hasSignedUp
				) {
					gtag('event', 'conversion', {
						send_to: 'AW-11468082525/aNs-COCo7YkZEN2atNwq',
					});
				}
			};

			// Append the script tag to the head
			document.head.appendChild(script);
		};

		// Add the conversion script only if the user has just finished signing up
		if (
			(location.pathname === '/providers' ||
				location.pathname === '/profile') &&
			hasSignedUp
		) {
			addConversionScript();
			localStorage.removeItem('hasSignedUp');
		}
	}, [hasSignedUp, location.pathname]);



	if (isFetchingUser) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<p className="text-gray-500">Loading your profile...</p>
				<Loader className="ml-4" />
			</div>
		);
	}

	if (shouldForceAthenaFlow) {
		return (
			<Routes>
				<Route path="/athena/welcome" element={<AthenaWelcome />} />
				<Route path="/athena/info" element={<AthenaPatientInfo />} />
				<Route path="/policy" element={<Policy />} />
				<Route path="/terms" element={<Terms />} />
				<Route path="*" element={<Navigate to="/athena/welcome" />} />
			</Routes>
		);
	}

	const homePath = {
		customer: '/jobs/create/athena',
		provider: '/jobs',
		partner: '/patients',
	}[user.isCustomer ? 'customer' : user.isProvider ? 'provider' : user.isPartner ? 'partner' : 'default'];

	// Routes for partner without access to platform
	// if (isAuth && user.isPartner && !user.partnerAccess) {
	// 	return (
	// 		<Routes>
	// 			<Route path="/auth/hold" element={<AwaitingApprovalPage />} />
	// 			{/* <Route path="/about" element={<AboutUs />} /> */}
	// 			<Route path="/about" element={<About />} />
	// 			<Route path="/auth" element={<Landing />} />
	// 			<Route path="/about/caregivers" element={<ForProviders />} />
	// 			<Route path="/about/how" element={<HowItWorks />} />
	// 			<Route path="/about/safety" element={<Safety />} />
	// 			<Route path="*" element={<Navigate to="/auth/hold" />} />
	// 		</Routes>
	// 	);
	// }

	return isAuth ? (
		accessToPlatform ? (
			<Routes>
				<Route path="/" element={<Navigate to={homePath} />} />

				<Route path="/eligibility" element={<CheckEligibility />} />
				<Route path="/consent" element={<ConsentUpload />} />

				<Route path="/jobs/athena" element={<AthenaJobs />} />
				<Route path="/jobs/create/athena" element={<JobCreateAthena />} />

				<Route path="/about" element={<About />} />
				<Route path="/about/how" element={<HowItWorks />} />
				<Route path="/about/safety" element={<Safety />} />
				<Route path="/policy" element={<Policy />} />
				<Route path="/terms" element={<Terms />} />
				<Route path="*" element={<Navigate to="/" />} />
				<Route path="/profile" element={<ProfileEdit />} />
				<Route path="/athena/welcome" element={<AthenaWelcome />} />
				<Route path="/athena/info" element={<AthenaPatientInfo />} />



				{user.isPartner &&
				{/* <>
						<Route path="/patients" element={<PatientsManage />} />
						<Route path="/patients/create" element={<PatientCreate />} />
						<Route path="/patients/:id" element={<PatientAbout />} />
						<Route path="/patients/:id/edit" element={<ProfileEdit />} />
					</> */}
				}

				{/* <>
					<Route path="/jobs" element={<CalendarProvider />} />
					<Route path="/jobs/my" element={<MyOrders />} />


					<Route path="/jobs/:id" element={<JobsDetails />} />
					<Route path="/jobs/:id/edit" element={<JobCreateProvider />} />
					<Route path="/jobs/:id/reschedule" element={<JobReschedule />} />
					<Route
						path="/jobs/create/:providerId"
						element={<JobCreateProvider />}
					/>
					<Route path="/jobs/:id/bids" element={<OrderBids />} />
					<Route path="/bids" element={<MyBids />} />

					
					<Route path="/customers/:id" element={<Profile />} />
					<Route path="/providers/:id" element={<Profile />} />
					<Route path="/patients/:id" element={<Profile />} />

					<Route path="/chats" element={<Chats />} />
					<Route path="/about/caregivers" element={<ForProviders />} />
					<Route path="/auth/caregivers/:type" element={<CaregiverProfiles />} />
				</> */}

			</Routes>
		) : (
			<Routes>
				<Route path="/policy" element={<Policy />} />
				<Route path="/terms" element={<Terms />} />
				<Route path="/about" element={<About />} />
				<Route path="/about/caregivers" element={<ForProviders />} />
				<Route path="/about/how" element={<HowItWorks />} />
				<Route path="/about/safety" element={<Safety />} />
				<Route path="*" element={<Navigate to="/profile" />} />




				{/* <Route path="/profile" element={<ProfileEdit />} /> */}
				{/* <Route path="/about" element={<AboutUs />} /> */}
			</Routes>
		)
	) : (
		<>
			<Routes>
				<Route path="/auth/signup" element={<VerificationGates />} />
				<Route path="/auth/login" element={<Auth />} />
				{/* <Route path="/test" element={<VerificationGates />} /> */}
				<Route path="/policy" element={<Policy />} />
				<Route path="/terms" element={<Terms />} />
				<Route path="/auth" element={<LandingTherapy />} />
				<Route path="/about" element={<About />} />
				<Route path="/services" element={<Services />} />
				<Route path="/about/caregivers" element={<ForProviders />} />
				<Route path="/about/how" element={<HowItWorks />} />
				<Route path="/about/safety" element={<Safety />} />
				<Route path="/unsubscribe/:token" element={<EmailUnsubscribe />} />
				<Route path="*" element={<Navigate to="/auth" />} />




				{/* <Route path="/about" element={<AboutUs />} /> */}
				{/* <Route path='/:type/waitlist' element={<Waitlist />} /> */}
				{/* <Route path="/auth" element={<Landing />} /> */}
				{/* <Route path="/auth/new" element={<NewAuth />} /> */}
				{/* <Route path="/auth/caregivers/:type" element={<CaregiverProfiles />} /> */}
				{/* <Route path="/auth/partner/signup" element={<PartnerSignup />} /> */}
			</Routes>
		</>
	);
});

const AutoTopScroll = () => {
	const location = useLocation();

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [location]);
};

const AppRoot = observer(() => {
	const hideFooterHeaderRoutes = ['/auth', '/auth/caregivers', '/auth/caregivers/customer', '/auth/caregivers/caregiver', '/auth/caregivers/partner', '/auth/customer', '/auth/caregiver', '/auth/partner/signup', '/auth/customer/signup', '/auth/signup', '/auth/caregiver/signup', '/auth/login', '/about', '/auth/hold', '/about/how', '/test'];
	const [currentRoute, setCurrentRoute] = useState('');
	const hideHeaderFooter = hideFooterHeaderRoutes.includes(currentRoute);
	return (
		// <PayPalScriptProvider
		// 	options={{
		// 		'client-id': PaypalClientId,
		// 		'data-partner-attribution-id': PaypalAttributionId,
		// 		'enable-funding': 'applepay',
		// 	}}
		// >
		<GoogleOAuthProvider clientId={GoogleClientId}>
			<BrowserRouter>
				<div className="app">
					{/* <Header /> */}
					{!hideHeaderFooter && <Header />}
					<Router setCurrentRoute={setCurrentRoute} />
					{!hideHeaderFooter && <Footer />}
					{/* <Footer /> */}
					{/* <Notifications /> */}
					<AutoTopScroll />
				</div>
			</BrowserRouter>
		</GoogleOAuthProvider>
		// </PayPalScriptProvider> 
	);
});

export default AppRoot;
